
import { Navigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Spinner } from "@/components/ui/spinner";
import { useToast } from "@/hooks/use-toast";
import { useEffect } from "react";

interface ProtectedRouteProps {
  children?: React.ReactNode;
  requiredRole?: string;
  guestAllowed?: boolean;
  companyOnly?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole,
  guestAllowed = false,
  companyOnly = false
}) => {
  const { user, loading, isAdmin, isCompany } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  
  // Reduced logging for security - only log essential auth flow info
  console.log("ProtectedRoute - Auth check:", {
    path: location.pathname,
    isLoading: loading,
    hasUser: !!user,
    isAdmin,
    isCompany
  });
  
  // Helper function to check if we're on an auth page
  const isAuthPage = (path: string): boolean => {
    return path.includes('/login') || 
           path.includes('/signup') || 
           path.includes('/company-login') || 
           path.includes('/company-signup');
  }

  // Use useEffect to show toast notifications instead of during render
  useEffect(() => {
    if (loading) return;
    
    // Only show toast if not on auth page and user is logged in but doesn't have access
    if (!isAuthPage(location.pathname) && user) {
      if (companyOnly && !isCompany) {
        toast({
          title: "Company access required",
          description: "This page is only accessible to company accounts",
          variant: "destructive"
        });
      } else if (requiredRole === "admin" && !isAdmin) {
        toast({
          title: "Access denied",
          description: "You don't have permission to access this page",
          variant: "destructive"
        });
      }
    } else if (!guestAllowed && !user && !isAuthPage(location.pathname)) {
      toast({
        title: "Authentication required",
        description: "Please log in to access this page",
      });
    }
  }, [loading, user, isCompany, isAdmin, companyOnly, requiredRole, location.pathname, guestAllowed, toast]);

  // Don't decide on routing while still loading
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  // Check authentication requirement
  if (!guestAllowed && !user) {
    const currentPath = encodeURIComponent(location.pathname + location.search);
    return companyOnly 
      ? <Navigate to={`/company-login?from=${currentPath}`} replace />
      : <Navigate to={`/login?from=${currentPath}`} replace />;
  }
  
  // Check company access requirement
  if (companyOnly && (!user || !isCompany)) {
    return <Navigate to="/" replace />;
  }
  
  // Check admin role requirement
  if (requiredRole === "admin" && (!user || !isAdmin)) {
    return <Navigate to="/" replace />;
  }

  // If all checks pass, render the route content
  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;
